import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MessageCircle, Hash, BarChart2, Play, ArrowLeft } from "lucide-react";

const outputs = [
  {
    num: "01",
    title: "Estimated Graft Count",
    desc: "AI-computed estimate of the number of grafts detected within the analyzed scalp image area.",
  },
  {
    num: "02",
    title: "Density Distribution Map",
    desc: "Visual breakdown of implantation density — clearly distinguishing high, medium, and low-density zones.",
  },
  {
    num: "03",
    title: "Verification Video",
    desc: "A visual walkthrough of the analysis process — from raw image to final graft count and density output.",
  },
];

export default function GcAboutPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#060C18] text-white overflow-x-hidden">
      {/* Background glow */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 80% 50% at 50% 0%, rgba(37,99,235,0.12) 0%, transparent 60%)",
        }}
      />

      {/* Grid */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(rgba(37,99,235,0.03) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(37,99,235,0.03) 1px, transparent 1px)`,
          backgroundSize: "72px 72px",
        }}
      />

      {/* Navigation */}
      <header className="relative z-10 px-6 py-5 border-b border-white/8">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-3 focus:outline-none group"
          >
            <img
              src="/assets/images/graftcounter-logo.png"
              alt="Graft Counter"
              className="h-9 w-9 object-contain rounded-xl"
            />
            <span className="text-base font-semibold tracking-tight group-hover:text-white/80 transition-colors">
              Graft Counter
            </span>
          </button>

          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-2 text-sm text-white/50 hover:text-white/80 transition-colors font-medium"
            >
              <ArrowLeft size={14} />
              Back to Home
            </button>
            <button
              onClick={() => navigate("/login-screen")}
              className="text-sm text-white/60 hover:text-white transition-colors font-medium px-4 py-2 border border-white/10 hover:border-white/25 rounded-lg"
            >
              Sign In
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 py-20 sm:py-28 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65 }}
          >
            <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full border border-blue-500/25 bg-blue-500/10 text-blue-300 text-xs font-medium tracking-[0.12em] uppercase mb-9">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse inline-block flex-shrink-0" />
              Post-Operation Analysis
            </div>

            <h1
              className="font-semibold tracking-tight leading-tight mb-6"
              style={{ fontSize: "clamp(2rem, 5vw, 3.4rem)" }}
            >
              Verify your hair transplant with objective AI analysis
            </h1>

            <p className="text-slate-400 text-lg leading-relaxed max-w-2xl mx-auto">
              Upload a post-operation photograph and receive an AI-estimated
              graft count, density distribution map, and a professional
              verification report — in minutes.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Analysis images */}
      <section className="relative z-10 pb-4 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-5">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:border-blue-500/20 transition-colors duration-300"
            >
              <div className="px-5 py-3 border-b border-white/8 flex items-center gap-2">
                <Hash size={12} className="text-blue-400" />
                <p className="text-white/50 text-xs font-medium tracking-wider uppercase">
                  Graft Detection Overlay
                </p>
              </div>
              <img
                src="/assets/images/overlay.png"
                alt="AI graft detection overlay"
                className="w-full object-cover"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:border-blue-500/20 transition-colors duration-300"
            >
              <div className="px-5 py-3 border-b border-white/8 flex items-center gap-2">
                <BarChart2 size={12} className="text-blue-400" />
                <p className="text-white/50 text-xs font-medium tracking-wider uppercase">
                  Density Heatmap
                </p>
              </div>
              <img
                src="/assets/images/heatmap.png"
                alt="Density distribution heatmap"
                className="w-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Verification Video — centered below images */}
      <section className="relative z-10 py-5 px-6">
        <div className="max-w-5xl mx-auto flex flex-col items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="w-full max-w-sm bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:border-blue-500/20 transition-colors duration-300"
          >
            <div className="px-5 py-3 border-b border-white/8 flex items-center gap-2">
              <Play size={12} className="text-blue-400" />
              <p className="text-white/50 text-xs font-medium tracking-wider uppercase">
                Verification Video
              </p>
            </div>
            <div className="bg-black/30">
              <video
                src="/assets/images/video_output.mov"
                autoPlay
                loop
                muted
                playsInline
                className="w-full rounded-b-2xl"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* What you receive */}
      <section className="relative z-10 py-12 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white/5 border border-white/10 rounded-2xl p-7 sm:p-10"
          >
            <h2 className="text-lg font-semibold mb-8 text-white">
              What you receive
            </h2>

            <div className="grid sm:grid-cols-3 gap-6">
              {outputs.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <div className="w-8 h-8 bg-blue-500/15 border border-blue-500/20 rounded-lg flex items-center justify-center mb-3">
                    <span className="text-blue-400 text-xs font-mono font-medium">
                      {item.num}
                    </span>
                  </div>
                  <h3 className="text-white font-semibold text-sm mb-2">
                    {item.title}
                  </h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    {item.desc}
                  </p>
                </motion.div>
              ))}
            </div>

            <p className="text-slate-600 text-xs leading-relaxed mt-8 pt-6 border-t border-white/8">
              Analysis results are for informational and documentation purposes
              only. The AI model uses deep learning with an inherent accuracy
              margin and is not a substitute for professional medical judgment.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 py-12 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-blue-600/20 to-indigo-600/10 border border-blue-500/20 rounded-2xl p-8 sm:p-12"
          >
            <div className="max-w-xl">
              <h2 className="text-2xl sm:text-3xl font-semibold mb-3 tracking-tight">
                Ready to analyze your results?
              </h2>
              <p className="text-slate-400 text-base leading-relaxed mb-8">
                Start your analysis with a single photograph. Reports are
                generated within seconds.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate("/payment")}
                  className="group flex items-center justify-center gap-2 px-7 py-3.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-medium text-sm transition-all duration-200"
                  style={{ boxShadow: "0 0 30px rgba(37,99,235,0.3)" }}
                >
                  Get Analysis Report
                  <ArrowRight
                    size={14}
                    className="group-hover:translate-x-0.5 transition-transform"
                  />
                </motion.button>

                <a
                  href="https://wa.me/905307836610?text=Hello%2C%20I%20would%20like%20to%20learn%20more%20about%20Graft%20Counter."
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-center gap-2 px-7 py-3.5 bg-white/5 hover:bg-white/10 text-white border border-white/15 hover:border-white/25 rounded-xl font-medium text-sm transition-all duration-200"
                >
                  <MessageCircle size={14} />
                  Contact via WhatsApp
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/5 px-6 py-8 mt-4">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-3 group"
          >
            <img
              src="/assets/images/graftcounter-logo.png"
              alt="Graft Counter"
              className="h-6 w-6 object-contain rounded-lg"
            />
            <span className="text-sm font-medium text-white/60 group-hover:text-white/80 transition-colors">
              Graft Counter
            </span>
          </button>
          <p className="text-slate-700 text-xs">
            © {new Date().getFullYear()} Graft Counter. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
